// src/components/LocationPicker.jsx
//
// Address picker used by the complete-profile location step:
//  - Nominatim autocomplete suggestions as the user types (LocationInput)
//  - "Use my current location" via the browser's GPS (reverse-geocoded)
//  - A Leaflet/OpenStreetMap map the user can click to drop the pin
// Every pick resolves to { lat, lon, name } and is reported via onSelect.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LocationInput from './LocationInput';
import { reverseGeocode, getPositionWithRetry } from '../utils/geo';

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const makePinIcon = () =>
  L.divIcon({
    className: 'paymeo-map-pin',
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#dc2626;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });

const LocationPicker = ({ initialQuery = '', onSelect }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [location, setLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  // Keep the query in sync when the initial value changes (e.g. the modal is
  // reopened with a newly saved address).
  useEffect(() => {
    setQuery(initialQuery || '');
  }, [initialQuery]);

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Resolve a coordinate into a named location and report it to the parent.
  const setPicked = useCallback(async (lat, lon) => {
    const name = await reverseGeocode(lat, lon);
    const loc = { lat, lon, name };
    setLocation(loc);
    setQuery(name);
    setError(null);
    onSelectRef.current?.(loc);
    return loc;
  }, []);

  // Initialize the Leaflet map once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [9.0579, 7.4951], // Nigeria center as a sensible default
      zoom: 6,
    });
    L.tileLayer(OSM_TILE_URL, { attribution: OSM_ATTRIBUTION, maxZoom: 19 }).addTo(map);
    map.on('click', (e) => {
      setPicked(e.latlng.lat, e.latlng.lng);
    });
    markerRef.current = L.marker([9.0579, 7.4951], { icon: makePinIcon(), zIndexOffset: 500 }).addTo(map);
    mapRef.current = map;

    requestAnimationFrame(() => map.invalidateSize());

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [setPicked]);

  // Move the marker and zoom when a location is picked.
  useEffect(() => {
    if (location && mapRef.current && markerRef.current) {
      markerRef.current.setLatLng([location.lat, location.lon]);
      mapRef.current.setView([location.lat, location.lon], 16);
    }
  }, [location]);

  const useMyLocation = async () => {
    setLocating(true);
    setError(null);
    try {
      const pos = await getPositionWithRetry();
      await setPicked(pos.coords.latitude, pos.coords.longitude);
    } catch (e) {
      setError(
        e.message?.includes('denied')
          ? 'Location permission denied. Type your address or click the map instead.'
          : 'Could not get your location. Type your address or click the map instead.'
      );
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <LocationInput
            value={query}
            onChange={setQuery}
            onSelect={(loc) => {
              // Forward the picked suggestion up to the parent too, so buttons
              // gated on the selected location (e.g. Save Address) activate.
              setLocation(loc);
              onSelectRef.current?.(loc);
            }}
            placeholder="Type your address or landmark"
          />
        </div>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 transition-colors"
          title="Use my current location"
        >
          {locating ? (
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
          <span className="hidden sm:inline">My location</span>
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {location ? (
        <p className="text-xs text-gray-600 truncate">
          <span className="text-red-600 font-medium">●</span> {location.name}
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Pick an address from the suggestions, use your current location, or click anywhere on the map.
        </p>
      )}

      <div
        ref={containerRef}
        className="w-full h-52 rounded-xl overflow-hidden border border-gray-200 z-0"
        aria-label="Map — click to set your business address"
      />
    </div>
  );
};

export default LocationPicker;
